import React, { useMemo } from "react";
import { LockOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { hasLicensedModule } from "../../core/module-license";
import { HUBS } from "./hub-config";
import "./section-hub.css";

const IS_PROD = process.env.NODE_ENV === "production";

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const SectionHub = ({ hub }) => {
  const navigate = useNavigate();
  const law = useSelector((state) => state.user.session?.law);
  const license = useSelector((state) => state.license.data);
  const licensed = hub.module == null || hasLicensedModule(license, hub.module);

  const tiles = useMemo(() => {
    const list = IS_PROD ? hub.tiles.filter((t) => !t.unimplemented) : hub.tiles;
    return list.map((tile) => {
      const tileLicensed =
        tile.module == null || hasLicensedModule(license, tile.module);
      const locked =
        tile.unimplemented ||
        !licensed ||
        !tileLicensed ||
        (tile.law != null && getLawFlag(law, tile.law) !== 1);
      return { ...tile, locked };
    });
  }, [hub, law, license, licensed]);

  if (!licensed) return <Navigate to="/" replace />;

  const featured = tiles.filter((t) => t.featured);
  const ready = tiles.filter((t) => !t.featured && !t.unimplemented);
  const soon = tiles.filter((t) => t.unimplemented);

  const openTile = (tile) => {
    if (tile.locked || !tile.path) return;
    if (tile.external || /^https?:\/\//i.test(tile.path)) {
      window.open(tile.path, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(tile.path);
  };

  return (
    <div className="section-hub">
      <header className="section-hub__hero">
        <div className="section-hub__hero-main">
          <div className="section-hub__hero-copy">
            <p className="section-hub__eyebrow">{hub.eyebrow}</p>
            <h1 className="section-hub__title">{hub.title}</h1>
          </div>
        </div>
        {hub.lead ? <p className="section-hub__lead">{hub.lead}</p> : null}
      </header>

      <section className="section-hub__grid" aria-label={`Формы раздела ${hub.title}`}>
        {featured.map((tile) => (
          <HubCard key={tile.key} tile={tile} featured onOpen={openTile} />
        ))}
        {ready.map((tile) => (
          <HubCard key={tile.key} tile={tile} onOpen={openTile} />
        ))}
      </section>

      {soon.length ? (
        <section className="section-hub__soon" aria-label="В разработке">
          <h2 className="section-hub__soon-title">Скоро</h2>
          <div className="section-hub__soon-grid">
            {soon.map((tile) => (
              <HubCard key={tile.key} tile={tile} compact onOpen={openTile} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

const HubCard = ({ tile, featured, compact, onOpen }) => {
  const Icon = tile.icon;
  const locked = tile.locked;
  const className = [
    "section-hub__card",
    featured ? "section-hub__card--featured" : "",
    compact ? "section-hub__card--compact" : "",
    locked ? "section-hub__card--locked" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      disabled={locked}
      onClick={() => onOpen(tile)}
    >
      <span className="section-hub__icon" aria-hidden>
        <Icon />
      </span>
      <span className="section-hub__card-text">
        <span className="section-hub__card-title">{tile.title}</span>
        <span className="section-hub__card-hint">{tile.hint}</span>
      </span>
      {tile.unimplemented ? <span className="section-hub__badge">Скоро</span> : null}
      {!tile.unimplemented && locked ? <LockOutlined className="section-hub__lock" /> : null}
    </button>
  );
};

export const SectionHubPage = () => {
  const { section } = useParams();
  const hub = HUBS[section];
  if (!hub) return <Navigate to="/" replace />;
  return <SectionHub hub={hub} />;
};

export default SectionHub;
